import { useEffect, useState, useCallback, useRef } from "react";
import { Providers } from "@tripian/model";
import { providers, API } from "@tripian/core";
import type { KiwiSortOption, KiwiHotelFilters } from "@tripian/react";
import { EMPTY_FILTERS } from "@tripian/react";
import moment from "moment";
import { useLanguage } from "../App/LanguageProvider/LanguageProvider";

const ITEMS_PER_PAGE = 20;

// Kiwi only understands English city names; the trip's city.name is localized
// per active language, so it's resolved to English here without touching the
// shared api singleton's language state.
const kiwiEnglishCityNameCache = new Map<number, string>();
let kiwiEnglishApi: API | null = null;
const getKiwiEnglishApi = (): API => {
  if (!kiwiEnglishApi) {
    kiwiEnglishApi = new API(
      { url: window.tconfig.TRIPIAN_API_URL, xApiKey: window.tconfig.TRIPIAN_API_KEY },
      "en",
      true
    );
  }
  return kiwiEnglishApi;
};

const useKiwiSearch = (
  cityId?: number,
  cityName?: string,
  arrivalDatetime?: string,
  departureDatetime?: string,
  numberOfAdults?: number,
  numberOfChildren?: number
) => {
  const { langCode } = useLanguage();

  // Editable search state (initialized from trip data)
  const [destination, setDestination] = useState<string>("");
  const [checkInDate, setCheckInDate] = useState<string>("");
  const [checkOutDate, setCheckOutDate] = useState<string>("");
  const [adults, setAdults] = useState<number>(1);
  const [childrenCount, setChildrenCount] = useState<number>(0);

  const [kiwiHotels, setKiwiHotels] = useState<Providers.Kiwi.KiwiSearchHotel[]>([]);
  const [loadingKiwiSearch, setLoadingKiwiSearch] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [kiwiSearchError, setKiwiSearchError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);

  // Sort & Filter
  const [sortBy, setSortBy] = useState<KiwiSortOption>("");
  const [filters, setFilters] = useState<KiwiHotelFilters>(EMPTY_FILTERS);

  // Prevent duplicate requests
  const abortRef = useRef<boolean>(false);

  // Initialize from trip data once available
  useEffect(() => {
    if (initialized) return;

    if (numberOfAdults) setAdults(numberOfAdults);
    if (numberOfChildren !== undefined) setChildrenCount(numberOfChildren);

    if (arrivalDatetime) {
      const today = moment().startOf("day");
      const inDate = moment(arrivalDatetime).isBefore(today)
        ? today.format("YYYY-MM-DD")
        : moment(arrivalDatetime).format("YYYY-MM-DD");
      setCheckInDate(inDate);
    }

    if (departureDatetime) {
      const today = moment().startOf("day");
      const tomorrow = moment(today).add(1, "days");
      const outDate = moment(departureDatetime).isBefore(today)
        ? tomorrow.format("YYYY-MM-DD")
        : moment(departureDatetime).format("YYYY-MM-DD");
      setCheckOutDate(outDate);
    }

    if (!cityName) return;

    let cancelled = false;
    const applyDestination = (name: string) => {
      if (cancelled) return;
      setDestination(name);
      if (arrivalDatetime && departureDatetime) setInitialized(true);
    };

    // Kiwi expects English city names; only resolve via API when the active
    // language isn't English and we have a cityId to look it up with.
    if (langCode === "en" || !cityId) {
      applyDestination(cityName);
    } else {
      const cached = kiwiEnglishCityNameCache.get(cityId);
      if (cached) {
        applyDestination(cached);
      } else {
        getKiwiEnglishApi()
          .city(cityId)
          .then((enCity) => {
            const enName = enCity?.name || cityName;
            kiwiEnglishCityNameCache.set(cityId, enName);
            applyDestination(enName);
          })
          .catch(() => applyDestination(cityName));
      }
    }

    return () => {
      cancelled = true;
    };
  }, [cityId, cityName, arrivalDatetime, departureDatetime, numberOfAdults, numberOfChildren, initialized, langCode]);

  /** Build request from current state */
  const buildRequest = useCallback(
    (page: number): Providers.Kiwi.KiwiSearchRequest => {
      const req: Providers.Kiwi.KiwiSearchRequest = {
        city: destination,
        in_date: checkInDate,
        out_date: checkOutDate,
        adults,
        children: childrenCount,
        rooms: 1,
        limit: ITEMS_PER_PAGE,
        page,
      };

      // Sort
      if (sortBy) {
        req.sort_by = sortBy;
      }

      // Filters
      if (filters.locations.length > 0) req.country = filters.locations[0];
      if (filters.specialOffers) req.has_special_offer = true;
      if (filters.ratings.length > 0) req.star_rating = filters.ratings;
      if (filters.interests.length > 0) req.interests = filters.interests;
      if (filters.settings.length > 0) req.settings = filters.settings;
      if (filters.styles.length > 0) req.styles = filters.styles;
      if (filters.brands.length > 0) req.brand = filters.brands;

      return req;
    },
    [destination, checkInDate, checkOutDate, adults, childrenCount, sortBy, filters]
  );

  /** Fetch first page (resets list) */
  const fetchKiwiHotels = useCallback(async () => {
    if (!providers.kiwi || !destination || !checkInDate || !checkOutDate) return;

    abortRef.current = true; // cancel any in-flight loadMore
    setLoadingKiwiSearch(true);
    setKiwiSearchError(null);

    try {
      const request = buildRequest(1);
      const response = await providers.kiwi.search(request);

      if (response.success && Array.isArray(response.data)) {
        const available = response.data.filter((h) => h.available !== false);
        setKiwiHotels(available);
        setCurrentPage(1);
        setHasNextPage(response.pagination?.hasNext ?? false);
        setTotalItems(response.pagination?.totalItems ?? available.length);
      } else {
        setKiwiHotels([]);
        setHasNextPage(false);
        setTotalItems(0);
      }
    } catch (error: any) {
      console.error("Kiwi Search Error:", error);
      setKiwiSearchError(typeof error === "string" ? error : error?.message || "Unknown error");
      setKiwiHotels([]);
      setHasNextPage(false);
      setTotalItems(0);
    } finally {
      setLoadingKiwiSearch(false);
      abortRef.current = false;
    }
  }, [destination, checkInDate, checkOutDate, buildRequest]);

  /** Load next page (appends to list) */
  const loadMore = useCallback(async () => {
    if (!providers.kiwi || !hasNextPage || loadingMore || loadingKiwiSearch) return;

    const nextPage = currentPage + 1;
    setLoadingMore(true);
    abortRef.current = false;

    try {
      const request = buildRequest(nextPage);
      const response = await providers.kiwi.search(request);

      if (abortRef.current) return; // a new search was triggered, discard

      if (response.success && Array.isArray(response.data)) {
        const available = response.data.filter((h) => h.available !== false);
        setKiwiHotels(prev => [...prev, ...available]);
        setCurrentPage(nextPage);
        setHasNextPage(response.pagination?.hasNext ?? false);
        setTotalItems(response.pagination?.totalItems ?? 0);
      }
    } catch (error: any) {
      console.error("Kiwi LoadMore Error:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [hasNextPage, loadingMore, loadingKiwiSearch, currentPage, buildRequest]);

  // Auto-search on initial load
  useEffect(() => {
    if (initialized && destination && checkInDate && checkOutDate) {
      fetchKiwiHotels();
    }
  }, [initialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when sort or filters change
  useEffect(() => {
    if (initialized && destination && checkInDate && checkOutDate) {
      fetchKiwiHotels();
    }
  }, [sortBy, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    kiwiHotels,
    loadingKiwiSearch,
    loadingMore,
    kiwiSearchError,
    hasNextPage,
    totalItems,
    // Search bar state
    destination,
    setDestination,
    checkInDate,
    setCheckInDate,
    checkOutDate,
    setCheckOutDate,
    adults,
    setAdults,
    childrenCount,
    setChildrenCount,
    // Sort & Filter
    sortBy,
    setSortBy,
    filters,
    setFilters,
    // Actions
    searchKiwiHotels: fetchKiwiHotels,
    loadMore,
  };
};

export default useKiwiSearch;